import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { api } from '../api/client';
import { useAuth } from '../context/AuthContext.jsx';
import { Badge, Empty, Field, Spinner, STATUS_TONE, fmtDate, label } from '../components/ui.jsx';
import { useConfirm, useToast } from '../components/feedback.jsx';
import { t } from '../i18n/index.jsx';
import { invalidate } from '../api/cache.js';
import InvoiceForm from '../components/forms/InvoiceForm.jsx';
import InvoicePrint from '../components/InvoicePrint.jsx';
import { IconPlus } from '../components/icons.jsx';

function fmtMoney(v, currency) {
  return v == null ? '—' : `${Number(v).toLocaleString()} ${currency}`;
}

export default function InvoiceDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const confirm = useConfirm();
  const toast = useToast();
  const canEdit = user.role === 'admin' || user.role === 'manager';

  const [data, setData] = useState(null);
  const [err, setErr] = useState('');
  const [editing, setEditing] = useState(false);
  const [printing, setPrinting] = useState(false);
  const [addingPayment, setAddingPayment] = useState(false);
  const [recipients, setRecipients] = useState([]);
  const [ownCompanies, setOwnCompanies] = useState([]);
  const [projects, setProjects] = useState([]);

  async function load() {
    try { setData(await api.get(`/invoices/${id}`)); }
    catch (e) { setErr(e.message); }
  }
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);
  useEffect(() => {
    if (!canEdit) return;
    api.get('/companies').then((r) => setRecipients(r.data.filter((c) => c.type !== 'own'))).catch(() => {});
    api.get('/companies?type=own').then((r) => setOwnCompanies(r.data)).catch(() => {});
    api.get('/projects').then((r) => setProjects(r.data)).catch(() => {});
  }, [canEdit]);

  async function removePayment(paymentId) {
    if (!(await confirm({ title: t('Delete payment'), message: t('Delete this payment record?'), confirmLabel: t('Delete'), danger: true }))) return;
    await api.del(`/invoices/${id}/payments/${paymentId}`);
    toast(t('Payment deleted'));
    load();
  }

  if (err) return <div className="alert error">{err}</div>;
  if (!data) return <Spinner />;
  const inv = data.data;

  return (
    <div className="page fade-in">
      <div className="breadcrumb"><Link to="/invoices">{t('Invoices')}</Link> / {inv.number}</div>
      <div className="page-head">
        <div className="title-row">
          <h1 className="page-title">{inv.number}</h1>
          <Badge tone={STATUS_TONE[inv.status]}>{label(inv.status)}</Badge>
        </div>
        <div className="head-actions">
          <button className="btn ghost" onClick={() => setPrinting(true)}>{t('Print / PDF')}</button>
          {canEdit && <button className="btn ghost" onClick={() => setEditing(true)}>{t('Edit')}</button>}
        </div>
      </div>

      <div className="detail-grid">
        <div className="kv"><span>{t('Client')}</span><b>{inv.client_name}</b></div>
        <div className="kv"><span>{t('Project')}</span><b>{inv.project_name || '—'}</b></div>
        <div className="kv"><span>{t('Issue date')}</span><b>{fmtDate(inv.issue_date)}</b></div>
        <div className="kv"><span>{t('Due date')}</span><b>{fmtDate(inv.due_date)}</b></div>
        <div className="kv"><span>{t('Amount')}</span><b>{fmtMoney(inv.amount, inv.currency)}</b></div>
        <div className="kv"><span>{t('Balance due')}</span><b>{fmtMoney(data.balance, inv.currency)}</b></div>
      </div>
      {inv.description && <p className="description">{inv.description}</p>}

      <section className="panel">
        <div className="panel-head">
          <h2 className="panel-title">{t('Payments')}</h2>
          {canEdit && data.balance > 0.01 && (
            <button className="btn ghost sm" onClick={() => setAddingPayment(true)}>
              <IconPlus width={13} height={13} /> {t('Record payment')}
            </button>
          )}
        </div>
        {data.payments.length === 0 ? <Empty>{t('No payments recorded yet.')}</Empty> : (
          <ul className="line-list">
            {data.payments.map((pm) => (
              <li key={pm.id}>
                <div className="ll-main">
                  <span className="ll-title">{fmtMoney(pm.amount, pm.currency)}</span>
                  <span className="muted sm">
                    {fmtDate(pm.paid_at)}{pm.method ? ` · ${pm.method}` : ''}{pm.payer_name ? ` · ${pm.payer_name}` : ''}
                  </span>
                </div>
                <div className="ll-meta">
                  {canEdit && <button className="icon-btn xs" title={t('Remove')} onClick={() => removePayment(pm.id)}>×</button>}
                </div>
              </li>
            ))}
          </ul>
        )}
      </section>

      {editing && (
        <InvoiceForm
          initial={inv}
          recipients={recipients}
          ownCompanies={ownCompanies}
          projects={projects}
          onClose={() => setEditing(false)}
          onSaved={() => { invalidate('/invoices'); setEditing(false); load(); }}
        />
      )}

      {printing && (
        <PrintModal invoice={inv} onClose={() => setPrinting(false)} />
      )}

      {addingPayment && (
        <PaymentModal
          invoiceId={inv.id}
          currency={inv.currency}
          maxAmount={data.balance}
          clientId={inv.client_id}
          clientName={inv.client_name}
          onClose={() => setAddingPayment(false)}
          onSaved={() => { setAddingPayment(false); load(); }}
        />
      )}
    </div>
  );
}

function PrintModal({ invoice, onClose }) {
  return (
    <div className="modal-backdrop" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="print-modal">
        <div className="print-modal-bar no-print">
          <button className="btn ghost sm" onClick={onClose}>{t('Close')}</button>
          <button className="btn primary sm" onClick={() => window.print()}>{t('Print / Save as PDF')}</button>
        </div>
        <InvoicePrint invoice={invoice} />
      </div>
    </div>
  );
}

function PaymentModal({ invoiceId, currency, maxAmount, clientId, clientName, onClose, onSaved }) {
  const toast = useToast();
  const [amount, setAmount] = useState(maxAmount > 0 ? maxAmount.toFixed(2) : '');
  const [paidAt, setPaidAt] = useState(new Date().toISOString().slice(0, 10));
  const [method, setMethod] = useState('');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  async function save() {
    setBusy(true); setError('');
    try {
      await api.post(`/invoices/${invoiceId}/payments`, {
        amount: parseFloat(amount), currency, paid_at: paidAt, method, note, payer_id: clientId,
      });
      toast(t('Payment recorded'));
      onSaved();
    } catch (e) { setError(e.message); }
    finally { setBusy(false); }
  }

  return (
    <div className="modal-backdrop" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="modal" role="dialog" aria-modal="true">
        <div className="modal-head">
          <h3>{t('Record a payment')}</h3>
          <button className="icon-btn" onClick={onClose} aria-label={t('Close')}>×</button>
        </div>
        <div className="modal-body">
          {error && <div className="alert error">{error}</div>}
          <Field label={t('Payer')}><input value={clientName} disabled /></Field>
          <div className="grid-2">
            <Field label={t('Amount')}><input type="number" step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)} /></Field>
            <Field label={t('Date')}><input type="date" value={paidAt} onChange={(e) => setPaidAt(e.target.value)} /></Field>
          </div>
          <Field label={t('Method (optional)')}><input value={method} onChange={(e) => setMethod(e.target.value)} placeholder={t('bank transfer, cash…')} /></Field>
          <Field label={t('Note (optional)')}><textarea rows={2} value={note} onChange={(e) => setNote(e.target.value)} /></Field>
        </div>
        <div className="modal-foot">
          <div className="modal-actions">
            <div className="spacer" />
            <button className="btn ghost" onClick={onClose}>{t('Cancel')}</button>
            <button className="btn primary" onClick={save} disabled={busy || !amount}>{busy ? t('Saving…') : t('Save')}</button>
          </div>
        </div>
      </div>
    </div>
  );
}
